// [2026-09-08] ADDED — work out where to send an unauthenticated visitor WITHOUT being told.
//
// WHY THIS EXISTS
// ---------------
// The redirect to the InCruiter admin panel shipped needing a build arg, VITE_INCRUITER_ADMIN_URL,
// with no default. The reasoning was sound — guessing a hostname per environment could point a dev
// console at the production panel — but the consequence was not: dev was built without it, so the
// console silently did not redirect and showed a static screen instead. A feature that is off
// unless somebody remembers a build argument is off.
//
// So this derives the panel from something the console ALREADY has and cannot get wrong:
// VITE_API_BASE, the proctoring API it talks to. That value is per-environment and is already
// correct, because nothing in the app works if it is not.
//
//   https://dev.v1.proctoring-sdk-backend.incruiter.com   ->  https://dev.v1.admin.incruiter.com/proctoring
//
// THE DERIVATION IS DELIBERATELY NARROW, and this is the whole safety argument. It is the same
// shape as _admin_verify_url_from in proctoring-sdk-backend's config.py, which answered the same
// objection: the danger is a LOOSE derivation — appending, guessing, or falling back to the input
// when it does not fit. This one
//
//   · parses the URL rather than pattern-matching the string,
//   · requires EXACTLY ONE hostname label equal to `proctoring-sdk-backend`,
//   · replaces that one label with `admin` and rebuilds from the parsed parts,
//   · always emits the literal path /proctoring, never anything carried from the input,
//   · returns "" for anything it does not recognise.
//
// So the only thing surviving from the input is the rest of the hostname, which is what carries the
// environment. A dev API host can only ever produce a dev panel; there is no input that turns it
// into a different environment's, because no label except that one is touched. Anything unfamiliar
// yields "" and the console falls back to the static screen — the behaviour we have today, which is
// merely inconvenient rather than wrong.
//
// VITE_INCRUITER_ADMIN_URL STILL WINS when set. Deriving is the default, not the law: a deployment
// whose panel does not follow the convention states its own value and this never runs.

const API_LABEL = 'proctoring-sdk-backend'
const PANEL_LABEL = 'admin'
const PANEL_PATH = '/proctoring'

/**
 * Derive the InCruiter admin panel URL from the proctoring API base.
 *
 * @param {string} apiBase   VITE_API_BASE — the proctoring API origin. Empty for a same-origin
 *                           deployment, which yields "" because there is nothing to derive from.
 * @param {string} ownOrigin where this console is served from. Used only to refuse a derivation
 *                           that points back at ourselves.
 * @returns {string} an absolute URL, or "" when it cannot be derived safely.
 */
export function deriveInCruiterAdminUrl(apiBase, ownOrigin = '') {
  if (typeof apiBase !== 'string' || apiBase.trim() === '') return ''

  let url
  try {
    url = new URL(apiBase.trim())
  } catch {
    // Relative (same-origin deployment) or malformed. Nothing to derive from, and guessing from the
    // current page's host would be exactly the loose derivation this file refuses to be.
    return ''
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return ''

  const labels = url.hostname.split('.')
  const matches = labels.filter((label) => label === API_LABEL)
  // EXACTLY ONE. Zero means this is not a host we recognise. More than one is ambiguous, and an
  // ambiguous rewrite is a guess wearing a rule's clothes.
  if (matches.length !== 1) return ''

  const panelHost = labels.map((label) => (label === API_LABEL ? PANEL_LABEL : label)).join('.')

  // Rebuilt from the PARSED protocol and the rewritten host, with a literal path. Nothing is
  // concatenated onto the input string, so a query, a fragment, a port or a path on VITE_API_BASE
  // cannot travel into the result.
  const derived = `${url.protocol}//${panelHost}${PANEL_PATH}`

  // A derivation that points back at this console would be an infinite redirect the moment someone
  // is not signed in. The loop guard in BridgeSignIn would catch it after one bounce; not creating
  // it is better than catching it.
  try {
    if (ownOrigin && new URL(derived).origin === new URL(ownOrigin).origin) return ''
  } catch {
    // An unparseable ownOrigin is not a reason to discard a good derivation.
  }

  return derived
}

/**
 * The panel this console sends people to: the explicit setting if there is one, otherwise derived.
 *
 * @param {string} configured VITE_INCRUITER_ADMIN_URL. Wins whenever it is set — a deployment that
 *                            names its panel is stating the complete answer.
 */
export function resolveInCruiterAdminUrl(configured, apiBase, ownOrigin = '') {
  const explicit = (configured || '').trim()
  if (explicit) return explicit
  return deriveInCruiterAdminUrl(apiBase, ownOrigin)
}

// [2026-09-08] ADDED — where SIGNING OUT goes, which is a different page from where signing in goes.
//
// WHY SIGN-OUT NEEDS ITS OWN TARGET. This console has no identity of its own: its session is minted
// from an InCruiter one. So clearing only the local session is theatre — the InCruiter session is
// untouched, and clicking InC-Proctor again signs you straight back in without a prompt. "Sign out"
// that one click undoes has not signed anybody out of anything.
//
// So sign-out hands off to the InCruiter panel's /logout, which ends the session that actually
// matters and leaves the browser on the InCruiter login page.
//
// IT IS THE SAME ORIGIN AS THE SIGN-IN TARGET, only a different path — including when
// VITE_INCRUITER_ADMIN_URL is set to something with a path of its own (…/proctoring). Taking the
// origin and appending the literal path, rather than string-editing the configured value, is what
// stops a configured "…/proctoring" from becoming "…/proctoring/logout".
const LOGOUT_PATH = '/logout'

export function resolveInCruiterLogoutUrl(configured, apiBase, ownOrigin = '') {
  const base = resolveInCruiterAdminUrl(configured, apiBase, ownOrigin)
  if (!base) return ''
  try {
    // `ownOrigin` as the resolution base so a RELATIVE VITE_INCRUITER_ADMIN_URL still yields a
    // usable logout URL. Without it `new URL('/proctoring')` throws and sign-out silently stops
    // ending the InCruiter session — the worst kind of failure here, because the person is told
    // they signed out. A relative setting means "on this origin", and that is what it now means.
    const absolute = new URL(base, ownOrigin || undefined)
    return new URL(LOGOUT_PATH, absolute.origin).toString()
  } catch {
    // A configured value we cannot parse. Returning "" means sign-out falls back to clearing the
    // local session only — worse, but never a navigation to somewhere unintended.
    return ''
  }
}
